import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Clock, Calendar, GraduationCap, CheckCircle2, Briefcase, Euro, Accessibility, Target, BookOpen, Newspaper, ExternalLink, Award, ShieldCheck, Phone, MessageCircle } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/Accordion';
import { FORMATIONS, LINKS, PHONE_DISPLAY, PHONE_HREF, WHATSAPP_HREF, CFA_INFO, SITE_URL } from '../data/constants';
import { InserJeunes } from '../components/InserJeunes';
import { StructuredData } from '../components/StructuredData';
import { useSeo } from '../hooks/useSeo';
import { SocialProof } from '../components/SocialProof';
import { Cap360 } from '../components/Cap360';
export function FormationDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const formation = FORMATIONS.find(f => f.id === id);
  useSeo({
    title: formation ? `${formation.certification.intitule} en alternance — Joigny (89)` : 'Formation en alternance',
    description: formation ? `${formation.certification.intitule} en apprentissage à Joigny (Yonne). ${formation.duration}, ${formation.rhythm}. Formation gratuite pour l'apprenti, ${formation.rncp}. Rentrée ${formation.rentree}.` : 'Découvrez nos formations en alternance dans le commerce et la relation client.',
    path: `/formations/${id ?? ''}`
  });
  if (!formation) {
    return <Navigate to="/formations" replace data-id="element-1684" data-mp="2q0ak7sf452y4" />;
  }
  const courseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: formation.certification.intitule,
    description: formation.description,
    provider: {
      '@type': 'EducationalOrganization',
      name: CFA_INFO.name,
      sameAs: SITE_URL
    },
    educationalCredentialAwarded: formation.rncp,
    offers: {
      '@type': 'Offer',
      category: 'Apprenticeship',
      price: '0',
      priceCurrency: 'EUR',
      availability: 'https://schema.org/InStock',
      description: "Coût pris en charge par l'OPCO — 0 € pour l'apprenti"
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'blended',
      courseWorkload: formation.duration,
      location: {
        '@type': 'Place',
        name: CFA_INFO.name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: '25 rue Gabriel Cortel',
          postalCode: '89300',
          addressLocality: 'Joigny',
          addressCountry: 'FR'
        }
      }
    }
  };
  return <div className="flex flex-col w-full pb-24 bg-muted" data-id="element-1685" data-mp="1xz2qtneh74yl">
      <StructuredData id={`course-${formation.id}`} data={courseSchema} data-id="element-1686" data-mp="1xzkrcw8685cb" />

      {/* Custom Header for Formation */}
      <div className="relative bg-lavande py-16 md:py-24 overflow-hidden" data-id="element-1687" data-mp="1nx8f0joohl89">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[500px] h-[500px] bg-primary rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1688" data-mp="1ss49dqhe0koj" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-[300px] h-[300px] bg-brand-orange rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1689" data-mp="22r3j7cdw2md4" />

        <div className="container max-w-6xl mx-auto relative z-10" data-id="element-1690" data-mp="1a2xxonf0gtc8">
          <div className="flex flex-wrap gap-3 mb-8" data-id="element-1691" data-mp="1a14pv4snw32n">
            <Badge className="bg-primary text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1692" data-mp="1f1ixilqcpb05">
              {formation.level}
            </Badge>
            <Badge variant="outline" className="bg-white/50 border-primary/20 text-primary-dark text-sm px-4 py-1.5 font-semibold" data-id="element-1693" data-mp="1jv3m5n620smo">
              {formation.rncp}
            </Badge>
            <Badge variant="secondary" className="bg-brand-orange text-white text-sm px-4 py-1.5 font-bold border-none" data-id="element-1694" data-mp="2t1m39i7wjfvc">
              Session {formation.session}
            </Badge>
          </div>

          <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-primary-dark mb-6 tracking-tight leading-[1.1]" data-id="element-1695" data-mp="32tjfmsancdvn">
            {formation.title}
          </h1>

          <p className="text-xl md:text-2xl text-primary-dark/80 max-w-3xl leading-relaxed mb-10 font-medium" data-id="element-1696" data-mp="3brve1ghr8fyn">
            {formation.description}
          </p>

          <div className="flex flex-wrap gap-8 text-base font-medium text-primary-dark bg-white p-8 rounded-2xl shadow-xl inline-flex border-none" data-id="element-1697" data-mp="3lqt10ezj81fj">
            <div className="flex items-center gap-4 group" data-id="element-1698" data-mp="3gt1btsq9utf1">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1699" data-mp="1rcjow13y673q">
                <Clock className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1700" data-mp="1mf0b12b5vwcb" />
              </div>
              <div data-id="element-1701" data-mp="1wzcsivutn9g">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1702" data-mp="1xr77qdptey5">
                  Durée
                </span>
                <span className="font-bold" data-id="element-1703" data-mp="3ob8vcbd3s12b">{formation.duration}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1704" data-mp="3ta2b6afv445n"></div>
            <div className="flex items-center gap-4 group" data-id="element-1705" data-mp="3t893crtijdw2">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1706" data-mp="3f9j4p7r1p65l">
                <Calendar className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1707" data-mp="3k7kojuwap9i4" />
              </div>
              <div data-id="element-1708" data-mp="35er7i51dklbc">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1709" data-mp="3ab7uux0r2vnd">
                  Rythme
                </span>
                <span className="font-bold" data-id="element-1710" data-mp="13m31xp19dlse">{formation.rhythm}</span>
              </div>
            </div>
            <div className="w-px bg-slate-100 hidden sm:block" data-id="element-1711" data-mp="18j1va5d3lopj"></div>
            <div className="flex items-center gap-4 group" data-id="element-1712" data-mp="172o3ndwsejm2">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1713" data-mp="1748t4p48vcd2">
                <GraduationCap className="w-6 h-6 text-brand-orange animate-soft-bounce" data-id="element-1714" data-mp="x48910yjws7t" />
              </div>
              <div data-id="element-1715" data-mp="t3y3tqil25u0">
                <span className="block text-sm text-muted-foreground mb-1" data-id="element-1716" data-mp="t4hm58c5aw8k">
                  Rentrée
                </span>
                <span className="font-bold" data-id="element-1717" data-mp="1psnplrxbzil9">{formation.rentree}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-6xl mx-auto mt-16" data-id="element-1718" data-mp="1g34043f44qhf">
        <div className="grid lg:grid-cols-3 gap-12" data-id="element-1719" data-mp="1g1j6qh4jbdl5">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12" data-id="element-1720" data-mp="1c1sa3wsbu38p">
            <Tabs defaultValue="programme" className="w-full" data-id="element-1721" data-mp="28kghfj0nyxre">
              <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 mb-8 h-auto p-1" data-id="element-1722" data-mp="28lrjneegnxge">
                <TabsTrigger value="programme" className="py-3 text-sm md:text-base" data-id="element-1723" data-mp="23r3sf7s2ooy4">
                  Programme
                </TabsTrigger>
                <TabsTrigger value="modalites" className="py-3 text-sm md:text-base" data-id="element-1724" data-mp="3rh5i058dmq1b">
                  Modalités
                </TabsTrigger>
                <TabsTrigger value="debouches" className="py-3 text-sm md:text-base" data-id="element-1725" data-mp="488rq56p4543">
                  Débouchés
                </TabsTrigger>
                <TabsTrigger value="qualite" className="py-3 text-sm md:text-base" data-id="element-1726" data-mp="e6et8lolb98f">
                  Qualité
                </TabsTrigger>
              </TabsList>

              {/* TAB: Programme */}
              <TabsContent value="programme" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1727" data-mp="s0pdappn6f85">
                <section data-id="element-1728" data-mp="wwwfq8itx8lc">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1729" data-mp="7vlisb5ag8rj">
                    <Target className="w-6 h-6 text-primary" data-id="element-1730" data-mp="2x0qqz06wve6" />
                    Objectifs pédagogiques mesurables
                  </h2>
                  <div className="bg-slate-50 p-6 rounded-lg border border-slate-100" data-id="element-1731" data-mp="gv8mj3lhpqnr">
                    <p className="text-muted-foreground leading-relaxed" data-id="element-1732" data-mp="cvzt5zud3usq">
                      {formation.objectives}
                    </p>
                  </div>
                </section>

                <section data-id="element-1733" data-mp="3gcgcb0074gsy">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1734" data-mp="3gd892e5j4kkt">
                    <BookOpen className="w-6 h-6 text-primary" data-id="element-1735" data-mp="3u4mqwhnpafqa" />
                    Blocs de compétences
                  </h2>
                  <Accordion type="single" collapsible className="w-full" data-id="element-1736" data-mp="3paij5w6m23o5">
                    {formation.certification.blocs.map((bloc, i) => <AccordionItem key={i} value={`bloc-${i}`} data-id="element-1737" data-mp="2xk4aqfhd9jjv" data-mpi={i}>
                        <AccordionTrigger className="text-left font-semibold hover:text-primary" data-id="element-1738" data-mp="2nh8nv7ajzdbt">
                          {bloc.code}
                        </AccordionTrigger>
                        <AccordionContent data-id="element-1739" data-mp="2sa1rtsy76hmu">
                          <p className="text-muted-foreground mt-2" data-id="element-1740" data-mp="2djubpjvnxapb">
                            {bloc.description}
                          </p>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </section>
              </TabsContent>

              {/* TAB: Modalités */}
              <TabsContent value="modalites" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1741" data-mp="2w9kg8yp89auf">
                <section className="space-y-6" data-id="element-1742" data-mp="317c51juksgue">
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1743" data-mp="1kudmsw794bxu">
                    <div data-id="element-1744" data-mp="1ksu7b7tf64x4">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1745" data-mp="1kwqc5ufc9okx">Public visé</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1746" data-mp="1purwsjsexvyi">
                        {formation.public}
                      </p>
                    </div>
                    <div data-id="element-1747" data-mp="326oy9frasda8">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1748" data-mp="2nfzl47ua0o3q">Prérequis</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1749" data-mp="2ndvl40zi098q">
                        {formation.prerequisites}
                      </p>
                    </div>
                  </div>
                  <div data-id="element-1750" data-mp="2jeedb0wln00e">
                    <h3 className="text-lg font-bold mb-2" data-id="element-1751" data-mp="2jfpi941s6dt3">
                      Méthodes mobilisées
                    </h3>
                    <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1752" data-mp="24iyd0e5fe7vk">
                      {formation.methods}
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1753" data-mp="2ne948pjuhnn">
                    <div data-id="element-1754" data-mp="7nag8gtjmp7f">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1755" data-mp="7nsibu2vzxn9">
                        Évaluations en formation
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1756" data-mp="1n7t4d6j3kd0p">
                        {formation.evaluation}
                      </p>
                    </div>
                    <div data-id="element-1757" data-mp="1x5fn643ioopy">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1758" data-mp="1x44mwe9a5z3l">
                        Épreuve certificative
                      </h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1759" data-mp="2ayyjtkl4l3ah">
                        {formation.epreuve}
                      </p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-1760" data-mp="2fvxfw8ocnkbb">
                    <div data-id="element-1761" data-mp="qh9346buk0cr">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1762" data-mp="qgr10t2i6rwx">Accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1763" data-mp="li5wihg7xm2o">
                        {formation.acces}
                      </p>
                    </div>
                    <div data-id="element-1764" data-mp="2qj8lk1y39nnw">
                      <h3 className="text-lg font-bold mb-2" data-id="element-1765" data-mp="2qkjpq2vg4xfj">Délai d'accès</h3>
                      <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100 h-full" data-id="element-1766" data-mp="2ukltchkqs0xg">
                        {formation.delaiAcces}
                      </p>
                    </div>
                  </div>
                </section>
              </TabsContent>

              {/* TAB: Débouchés */}
              <TabsContent value="debouches" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1767" data-mp="39h33x6l3xbiy">
                <section data-id="element-1768" data-mp="3iaohimljcmqs">
                  <h2 className="text-2xl font-bold font-heading mb-6 flex items-center gap-2" data-id="element-1769" data-mp="3iaxy5jkoxhjt">
                    <Briefcase className="w-6 h-6 text-primary" data-id="element-1770" data-mp="3naat6rlptuxh" />
                    Débouchés officiels
                  </h2>
                  <div className="flex flex-wrap gap-3 mb-6" data-id="element-1771" data-mp="3s9neh7wxutxy">
                    {formation.opportunities.map((job, i) => <Badge key={i} variant="secondary" className="text-sm px-4 py-2" data-id="element-1772" data-mp="65bs3u7cxr3g" data-mpi={i}>
                        {job}
                      </Badge>)}
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 mb-6" data-id="element-1773" data-mp="3oeufb4xy7mzd">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1774" data-mp="ox4l3omffcb6">
                      Codes ROME correspondants :
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1775" data-mp="f7l1ggqw3ef9">
                      {formation.romeCodes.map((code, i) => <Badge key={i} variant="outline" className="bg-white" data-id="element-1776" data-mp="b5gc3u7h53yf" data-mpi={i}>
                          {code}
                        </Badge>)}
                    </div>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1777" data-mp="12w4aiyhrjv8e">
                    <span className="font-semibold text-sm block mb-2" data-id="element-1778" data-mp="new7u8qb2uw8">
                      Secteurs :
                    </span>
                    <p className="text-muted-foreground" data-id="element-1779" data-mp="sco0j4yryl1h">
                      {formation.secteurs}
                    </p>
                  </div>
                </section>

                <section data-id="element-1780" data-mp="ihcm6nra3fu9">
                  <h2 className="text-2xl font-bold font-heading mb-4 flex items-center gap-2" data-id="element-1781" data-mp="iino0i15ydiq">
                    <GraduationCap className="w-6 h-6 text-primary" data-id="element-1782" data-mp="1688hhuuy0t69" />
                    Poursuites d'études
                  </h2>
                  <p className="text-muted-foreground bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-1783" data-mp="xe3nx3ofd3n1">
                    {formation.furtherStudies}
                  </p>
                </section>
              </TabsContent>

              {/* TAB: Qualité */}
              <TabsContent value="qualite" className="space-y-10 animate-in fade-in-50 duration-500" data-id="element-1784" data-mp="1p2wxiikbninn">
                <section data-id="element-1785" data-mp="3eldb1z3h19b4">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1786" data-mp="3ekkdnoqejw5n">
                    Indicateurs de résultats
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4" data-id="element-1787" data-mp="3obyjee8y5s78">
                    {formation.indicators.map((ind, i) => <Card key={i} className="bg-primary/5 border-none" data-id="element-1788" data-mp="3jcbydimvn7nv" data-mpi={i}>
                        <CardContent className="p-6 text-center flex flex-col justify-center h-full" data-id="element-1789" data-mp="2vnav09swimpg">
                          <div className="text-3xl font-bold text-primary mb-2 font-heading" data-id="element-1790" data-mp="2qo7qqrkqwkij">
                            {ind.label}
                          </div>
                          <div className="text-sm font-medium text-foreground" data-id="element-1791" data-mp="35mryqloleqis">
                            {ind.value}
                          </div>
                        </CardContent>
                      </Card>)}
                  </div>
                </section>

                {formation.inserJeunes && <section data-id="element-1792" data-mp="mzldmiealg7e">
                    <InserJeunes data={formation.inserJeunes} data-id="element-1793" data-mp="mybcfkc57rwj" />
                  </section>}

                <section data-id="element-1794" data-mp="rx4rhh72vqyt">
                  <h2 className="text-2xl font-bold font-heading mb-6" data-id="element-1795" data-mp="rwlbi60zncnf">
                    Repères RNCP
                  </h2>
                  <Card className="border-none bg-slate-50" data-id="element-1796" data-mp="11wlyc1y2gawd">
                    <CardContent className="p-6 space-y-4" data-id="element-1797" data-mp="11vubbfijblhp">
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1798" data-mp="49mv18cvc4pi">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1799" data-mp="975737m6n01q">
                          Intitulé exact
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1800" data-mp="e4ybpw7u7lzw">
                          {formation.certification.intitule}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1801" data-mp="3hnhu870prdhi">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1802" data-mp="3ml06a6a128tq">
                          Niveau / Domaine
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1803" data-mp="1zplf2ps9n2i8">
                          {formation.certification.niveauDomaine}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1804" data-mp="1psgtar2c191y">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1805" data-mp="1ksm5wxhrn1ln">
                          Certificateur
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1806" data-mp="1g0ul73lajq3f">
                          {formation.certification.certificateur}
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1807" data-mp="1730t64uk7im2">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1808" data-mp="121uxpz9ilh8u">
                          Enregistrement
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1809" data-mp="2lxqpko7p5xs2">
                          {formation.certification.enregistrement}
                        </div>
                      </div>
                      {formation.certification.derniereDelivrance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1810" data-mp="2c1dwnsjz7nyd">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1811" data-mp="2h0h0j9o7zo4r">
                            Dernière délivrance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1812" data-mp="271884l906hki">
                            {formation.certification.derniereDelivrance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1813" data-mp="1y84p7xfl9cih">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1814" data-mp="1jbdxmac5p53f">
                          Capitalisation
                        </div>
                        <div className="sm:col-span-2 font-medium" data-id="element-1815" data-mp="14n58vse9t6j4">
                          {formation.certification.capitalisation}
                        </div>
                      </div>
                      {formation.certification.correspondance && <div className="grid sm:grid-cols-3 gap-4 border-b border-slate-200 pb-4" data-id="element-1816" data-mp="1ejqfgi5xupp2">
                          <div className="font-bold text-sm text-muted-foreground" data-id="element-1817" data-mp="19pcrv1xipmuy">
                            Correspondance
                          </div>
                          <div className="sm:col-span-2 font-medium" data-id="element-1818" data-mp="1jkef1s88geuw">
                            {formation.certification.correspondance}
                          </div>
                        </div>}
                      <div className="grid sm:grid-cols-3 gap-4" data-id="element-1819" data-mp="1se9jsz5g03a0">
                        <div className="font-bold text-sm text-muted-foreground" data-id="element-1820" data-mp="27btkvuz7xeaw">
                          Lien officiel
                        </div>
                        <div className="sm:col-span-2 font-medium break-all" data-id="element-1821" data-mp="229ul0v2oszip">
                          <a href={`https://${formation.certification.lien}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-id="element-1822" data-mp="2eskmr4j3ge2w">
                            {formation.certification.lien}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </TabsContent>
            </Tabs>

            {/* Points forts de la formation */}
            <Card className="border-none shadow-lg bg-white overflow-hidden" data-id="element-1823" data-mp="205j00mw80v6w">
              <div className="h-2 w-full bg-brand-orange" data-id="element-1824" data-mp="203elzc4lyfsw" />
              <CardHeader className="pt-8" data-id="element-1825" data-mp="1ceveuzxao92e">
                <CardTitle className="text-2xl font-extrabold font-heading text-primary-dark flex items-center gap-2" data-id="element-1826" data-mp="1cdu5czvxfums">
                  <Target className="w-6 h-6 text-brand-orange" data-id="element-1827" data-mp="yaoe2jgyz61z" />
                  Les métiers visés
                </CardTitle>
              </CardHeader>
              <CardContent className="p-8 pt-2" data-id="element-1828" data-mp="132o7dt2jb0pl">
                <p className="text-muted-foreground leading-relaxed mb-6" data-id="element-1829" data-mp="ocgzu8e2f23o">
                  À l'issue de cette formation, vous pourrez exercer sur de
                  nombreux postes recherchés par les entreprises :
                </p>
                <div className="grid sm:grid-cols-2 gap-3" data-id="element-1830" data-mp="1h27f4krfoa1d">
                  {formation.opportunities.map((job, i) => <div key={i} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3 border border-slate-100" data-id="element-1831" data-mp="1lxwikz6kqb2y" data-mpi={i}>
                      <CheckCircle2 className="w-5 h-5 text-primary shrink-0" data-id="element-1832" data-mp="1c08dpgf97n72" />
                      <span className="font-medium text-primary-dark text-sm" data-id="element-1833" data-mp="3rs5xwrnxh8rv">
                        {job}
                      </span>
                    </div>)}
                </div>
                <div className="mt-8 pt-6 border-t border-slate-100" data-id="element-1834" data-mp="2380597aclz1b">
                  <Button className="w-full group bg-brand-orange hover:bg-[#E67E22] text-white text-lg py-6 shadow-md" asChild data-id="element-1835" data-mp="1y9ow6yrvnsk5">
                    <Link to="/candidater" data-id="element-1836" data-mp="1okoq6veyjwwg">
                      Je postule à cette formation
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1837" data-mp="1jl2h8y65x2tg" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* CAP 360 — Accompagnement recherche d'alternance */}
            <Cap360 data-id="element-1838" data-mp="15lszhu2b60nd" />
          </div>

          {/* Sidebar / Sticky Actions */}
          <div className="lg:col-span-1" data-id="element-1839" data-mp="15nvyv4pcp64r">
            <div className="sticky top-28 space-y-8" data-id="element-1840" data-mp="34yx4k2pkqzx0">
              <Card className="border-none shadow-xl bg-white overflow-hidden" data-id="element-1841" data-mp="34waxs5aicofu">
                <div className="h-2 w-full bg-gradient-to-r from-primary to-brand-orange" data-id="element-1842" data-mp="300depr7v13zd" />
                <CardHeader className="bg-white pb-6 pt-8" data-id="element-1843" data-mp="37ijydfus56a3">
                  <CardTitle className="text-2xl font-extrabold font-heading text-center text-primary-dark" data-id="element-1844" data-mp="3chob2ggwxm56">
                    Intéressé(e) par cette formation ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8 pt-0 space-y-4" data-id="element-1845" data-mp="3m6o9nyri6fiu">
                  <Button className="w-full text-lg py-7 group bg-brand-orange hover:bg-[#E67E22] text-white shadow-md" asChild data-id="element-1846" data-mp="2okh68s6xqt82">
                    <Link to="/candidater" data-id="element-1847" data-mp="2yieu3ekvuo05">
                      Je candidate maintenant
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1848" data-mp="2ykrioal5bwm6" />
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full py-6 border-primary/20 text-primary-dark hover:bg-primary/5 font-semibold" asChild data-id="element-1849" data-mp="dree79i4c8k1">
                    <Link to="/qualite#indicateurs" data-id="element-1850" data-mp="georesz21ky4">
                      <Target className="w-5 h-5 mr-2" data-id="element-1851" data-mp="cc267aww2y9w" />
                      Voir nos indicateurs de résultats
                    </Link>
                  </Button>

                  <div className="pt-4 border-t border-slate-100 space-y-3" data-id="element-1852" data-mp="lboxv4ztgppr">
                    <p className="text-center text-sm font-semibold text-muted-foreground" data-id="element-1853" data-mp="ldrxmgqrtx7o">
                      Une question ? Contactez-nous directement
                    </p>
                    <a href={PHONE_HREF} className="flex items-center justify-center gap-3 bg-lavande hover:bg-lavande/70 text-primary-dark font-bold rounded-xl py-4 transition-colors" data-id="element-1854" data-mp="v9d2qnr4zdln">
                      <Phone className="w-5 h-5 text-primary shrink-0" data-id="element-1855" data-mp="1iyxotfil6qz5" />
                      {PHONE_DISPLAY}
                    </a>
                    <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-primary-dark hover:bg-primary text-white font-bold rounded-xl py-4 transition-colors" data-id="element-1856" data-mp="1e1nov5xkmwx2">
                      <MessageCircle className="w-5 h-5 text-brand-orange shrink-0" data-id="element-1857" data-mp="1nqcj6kr58gnj" />
                      Écrivez-nous sur WhatsApp
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg bg-white" data-id="element-1858" data-mp="1ipf3qfxiq0pq">
                <CardContent className="p-8 space-y-8" data-id="element-1859" data-mp="1iqzurvkmj1iu">
                  <div className="group" data-id="element-1860" data-mp="14yjq6qc2qvft">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1861" data-mp="14vnrv0wbe4qu">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1862" data-mp="uzb0i9o8s2z9">
                        <Euro className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1863" data-mp="zx2r90d5hj1v" />
                      </div>
                      Tarif et financement
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13" data-id="element-1864" data-mp="c2sldlcfjbmr">
                      {formation.price}
                    </p>
                  </div>

                  <div className="pt-8 border-t border-slate-100 group" data-id="element-1865" data-mp="24wghba35cx3">
                    <div className="flex items-center gap-3 font-extrabold text-lg text-primary-dark mb-3" data-id="element-1866" data-mp="22s0huywwngg">
                      <div className="w-10 h-10 rounded-lg bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-1867" data-mp="zqak60u439z8">
                        <Accessibility className="w-5 h-5 text-brand-orange animate-soft-pulse" data-id="element-1868" data-mp="ptfmtl9krfmj" />
                      </div>
                      Accessibilité handicap
                    </div>
                    <p className="text-muted-foreground leading-relaxed pl-13 mb-4" data-id="element-1869" data-mp="3aky6r958tdl4">
                      {formation.handicap}
                    </p>
                    <div className="pl-13" data-id="element-1870" data-mp="35npm91wmgxgp">
                      <Button variant="outline" size="sm" className="border-primary/20 text-primary-dark hover:bg-primary/5" asChild data-id="element-1871" data-mp="1l4c5kijau6q4">
                        <Link to="/accessibilite" data-id="element-1872" data-mp="1uzxc8tzbbv67">
                          En savoir plus
                          <ArrowRight className="w-4 h-4 ml-2" data-id="element-1873" data-mp="1zujr574ls5v1" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Réussite & Centre d'examen — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-muted" data-id="element-1874" data-mp="2ip8g4kbym5ok">
          <div className="container max-w-5xl mx-auto space-y-10" data-id="element-1875" data-mp="jf0bc6o0k76j">
            {/* 100% de réussite */}
            <div className="bg-primary-dark text-white rounded-3xl shadow-xl overflow-hidden relative" data-id="element-1876" data-mp="ocicvbzpbk4i">
              <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1877" data-mp="2q9sirjgabtqh" />
              <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 p-10 md:p-12" data-id="element-1878" data-mp="3016my4j6lhpy">
                <div className="shrink-0 text-center" data-id="element-1879" data-mp="2v1ai632ntm9c">
                  <div className="text-6xl md:text-7xl font-extrabold font-heading text-brand-orange leading-none" data-id="element-1880" data-mp="2v3wq43tgq3s3">
                    100%
                  </div>
                  <div className="flex items-center justify-center gap-1 mt-3 text-lavande font-bold uppercase tracking-wider text-sm" data-id="element-1881" data-mp="22e6jsgy2o66j">
                    <Award className="w-5 h-5" data-id="element-1882" data-mp="2h7t1cq1cynn0" />
                    de réussite
                  </div>
                </div>
                <div className="w-px h-24 bg-white/10 hidden md:block" data-id="element-1883" data-mp="3sot30c9lietp" />
                <div className="text-center md:text-left" data-id="element-1884" data-mp="3sr5jsm3maj56">
                  <h2 className="font-heading text-2xl md:text-3xl font-extrabold mb-3" data-id="element-1885" data-mp="3npg60yp0frde">
                    Session d'examen 2025 : 100 % de réussite
                  </h2>
                  <p className="text-lavande text-lg leading-relaxed" data-id="element-1886" data-mp="43pnlb4q0p9m">
                    L'intégralité de nos candidats au TP Conseiller de Vente a
                    obtenu sa certification lors de la session d'examen 2025. Un
                    résultat qui récompense l'accompagnement individualisé et la
                    pédagogie par l'action de Zillenium Alternance.
                  </p>
                </div>
              </div>
            </div>

            {/* Centre d'examen agréé */}
            <div className="bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden" data-id="element-1887" data-mp="93c17logo7iy">
              <div className="h-2 w-full bg-primary" data-id="element-1888" data-mp="d3cdazo386mu" />
              <div className="p-8 md:p-12" data-id="element-1889" data-mp="d1rid8xv2log">
                <div className="flex items-start gap-5 mb-6" data-id="element-1890" data-mp="10tgkfh0hioil">
                  <div className="w-14 h-14 rounded-2xl bg-lavande flex items-center justify-center shrink-0" data-id="element-1891" data-mp="15keysb1wxcj7">
                    <ShieldCheck className="w-7 h-7 text-primary" data-id="element-1892" data-mp="271oulct6s7fa" />
                  </div>
                  <div data-id="element-1893" data-mp="2b30ohuj3feta">
                    <span className="inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-2" data-id="element-1894" data-mp="2b28u2n18fn4l">
                      Centre d'examen agréé
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1895" data-mp="2krimy53wh9tr">
                      Zillenium, centre d'examen du titre professionnel
                    </h2>
                  </div>
                </div>

                <p className="text-muted-foreground leading-relaxed mb-8" data-id="element-1896" data-mp="1x393h9u5sy5s">
                  Conformément à l'article L. 335-6 du code de l'éducation, le
                  titre professionnel est une certification délivrée au nom du
                  ministre chargé de l'emploi qui atteste de compétences
                  directement opérationnelles en entreprise. La délivrance de
                  cette certification s'appuie sur le respect des modalités
                  d'examen fixées réglementairement. Dans ce cadre, Zillenium
                  veille à ce que le responsable de session ainsi que les
                  membres de jury habilités bénéficient d'une information
                  complète et actualisée des modalités d'examen décrites dans le
                  référentiel de certification.
                </p>

                <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6" data-id="element-1897" data-mp="j3xu3qe14v1x">
                  <h3 className="font-bold text-primary-dark mb-2" data-id="element-1898" data-mp="e41rnvkzdboh">
                    Décision de renouvellement d'agrément n° 0350 — 7 mai 2026
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4" data-id="element-1899" data-mp="97ba13tj280y">
                    Agrément initialement délivré le 13 mai 2025, renouvelé par
                    cette décision pour la période en cours.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-1900" data-mp="1yk6g5qvf37ri">
                    <div data-id="element-1901" data-mp="3t0wrlproed4a">
                      <span className="text-muted-foreground block mb-1" data-id="element-1902" data-mp="3szlo7r2577dp">
                        Autorité
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1903" data-mp="1na9qd2dczry">
                        Préfète de la région Bourgogne-Franche-Comté (DREETS)
                      </span>
                    </div>
                    <div data-id="element-1904" data-mp="3nzh65wll64ld">
                      <span className="text-muted-foreground block mb-1" data-id="element-1905" data-mp="fm03pjtw6jc3">
                        Validité totale de l'agrément
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1906" data-mp="kkbgo3fhh9yj">
                        Du 13 mai 2025 au 12 mai 2027
                      </span>
                    </div>
                    <div data-id="element-1907" data-mp="alm32mumebo3">
                      <span className="text-muted-foreground block mb-1" data-id="element-1908" data-mp="akubro82303m">
                        Lieu des sessions d'examen
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1909" data-mp="2vd4surcyz3ul">
                        25 rue Gabriel Cortel, 89300 Joigny
                      </span>
                    </div>
                    <div data-id="element-1910" data-mp="2iuiua6q4dlzs">
                      <span className="text-muted-foreground block mb-1" data-id="element-1911" data-mp="2it8wzjr3chu7">
                        Capacité par session
                      </span>
                      <span className="font-medium text-primary-dark" data-id="element-1912" data-mp="2xp6xfe81s8c8">
                        Jusqu'à 30 candidats
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>}

      {/* Dans la presse — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <section className="py-20 bg-white" data-id="element-1913" data-mp="2eui81zws46i6">
          <div className="container max-w-4xl mx-auto" data-id="element-1914" data-mp="2a1p59hkvf88q">
            <div className="text-center mb-10" data-id="element-1915" data-mp="2a046ffrix352">
              <div className="inline-flex items-center justify-center p-3 bg-lavande rounded-2xl mb-4" data-id="element-1916" data-mp="1mbcnleztpx4y">
                <Newspaper className="w-7 h-7 text-primary" data-id="element-1917" data-mp="1hci40au184js" />
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-primary-dark" data-id="element-1918" data-mp="kmphi0tk6wkc">
                Dans la presse
              </h2>
            </div>
            <a href="https://www.lyonne.fr/joigny-89300/actualites/il-y-a-un-manque-sur-joigny-des-etudiants-mobilises-pour-sensibiliser-au-don-du-sang-dans-la-galerie-marchande_14961315/" target="_blank" rel="noopener noreferrer" className="group block bg-white rounded-3xl shadow-lg border border-slate-100 overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-1919" data-mp="tmkxciy27fqp">
              <div className="flex flex-col sm:flex-row" data-id="element-1920" data-mp="1havouvhv832b">
                <div className="sm:w-48 shrink-0 bg-primary-dark flex flex-col items-center justify-center p-8 text-center" data-id="element-1921" data-mp="3e4p83zkt2vwe">
                  <Newspaper className="w-10 h-10 text-lavande mb-3" data-id="element-1922" data-mp="3j4l9dr24c989" />
                  <span className="text-white font-bold font-heading leading-tight" data-id="element-1923" data-mp="3bjr8kh2lh7pk">
                    L'Yonne Républicaine
                  </span>
                  <span className="text-lavande text-sm mt-1" data-id="element-1924" data-mp="36rzp0qhuw28x">lyonne.fr</span>
                </div>
                <div className="p-8 flex-1" data-id="element-1925" data-mp="31uhdcscgf8x8">
                  <span className="inline-block bg-brand-orange/10 text-brand-orange text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-4" data-id="element-1926" data-mp="2wvejm41xcp4k">
                    Engagement étudiant • Joigny
                  </span>
                  <h3 className="text-xl font-extrabold font-heading text-primary-dark mb-3 leading-snug group-hover:text-primary transition-colors" data-id="element-1927" data-mp="3pl3lskqdev7r">
                    « Il y a un manque sur Joigny » : des étudiants mobilisés
                    pour sensibiliser au don du sang dans la galerie marchande
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4" data-id="element-1928" data-mp="293ft4ctwmn6u">
                    Nos alternants s'engagent sur le terrain au service d'une
                    cause solidaire — une illustration concrète de la pédagogie
                    par l'action chez Zillenium Alternance.
                  </p>
                  <span className="inline-flex items-center text-primary font-bold group-hover:gap-2 transition-all" data-id="element-1929" data-mp="2bhup03gmree7">
                    Lire l'article
                    <ExternalLink className="w-4 h-4 ml-1" data-id="element-1930" data-mp="21kq19z74zava" />
                  </span>
                </div>
              </div>
            </a>
          </div>
        </section>}

      {/* Témoignages d'alternants — TP Conseiller de Vente */}
      {formation.id === 'tp-conseiller-vente' && <>
          <div className="mt-24" data-id="element-1931" data-mp="1dy053ehgyail">
            <SocialProof title="On les accompagne dans leurs projets de marque" subtitle="Au-delà du diplôme, nos conseillers de vente créent, lancent et défendent de vrais projets de création de marque." testimonials={[{
          url: 'https://www.instagram.com/reel/DXKberwjoL6/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }, {
          url: 'https://www.instagram.com/p/DXNAq0yjk7y/embed',
          name: 'Projet de création de marque',
          role: 'TP Conseiller de Vente'
        }]} featuredIndex={0} data-id="element-1932" data-mp="1ivjfu4eyk55s" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1933" data-mp="1492tlvkqqzb4">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1934" data-mp="1i07x58r8cbqq" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1935" data-mp="1e0qrae7w5cl1">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1936" data-mp="1ryot6k50zgjn">
                Envie de vivre l'aventure ?
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1937" data-mp="2ucjjd43gzmlg">
                Rejoignez le TP Conseiller de Vente et donnez vie à vos projets
                commerciaux dès la rentrée.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1938" data-mp="33d9cg01ciyvh">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1939" data-mp="38ark7n3mn81w">
                  <Link to="/candidater" data-id="element-1940" data-mp="2fkj2fwd06pnu">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1941" data-mp="2kjvls74o1elo" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1942" data-mp="1rw9iz139idzv">
                  <Link to="/entreprises" data-id="element-1943" data-mp="1kd8mv0usjmmj">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}

      {/* Témoignages d'alternants — BTS NDRC uniquement */}
      {formation.id === 'bts-ndrc' && <>
          <div className="mt-24" data-id="element-1944" data-mp="16e0ky078u0en">
            <SocialProof data-id="element-1945" data-mp="16f27p1xmgbc7" />
          </div>

          {/* CTA après témoignages */}
          <section className="py-20 bg-primary-dark text-white relative overflow-hidden" data-id="element-1946" data-mp="11mqvp0plwz3a">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary rounded-full blur-[120px] opacity-40 pointer-events-none" data-id="element-1947" data-mp="wnxqrwodygeq" />
            <div className="container max-w-3xl mx-auto text-center relative z-10" data-id="element-1948" data-mp="rn9vgyt4jdep">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold mb-6 tracking-tight" data-id="element-1949" data-mp="ms46ts8c7kmx">
                Convaincu(e) ? Rejoignez la prochaine promotion
              </h2>
              <p className="text-lavande text-lg md:text-xl mb-10 leading-relaxed font-medium" data-id="element-1950" data-mp="2bmybse1eci9j">
                Lancez votre candidature dès aujourd'hui et construisez votre
                avenir dans la relation client.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1951" data-mp="1xtyrlyqo5zvn">
                <Button size="lg" className="w-full sm:w-auto group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-10 py-7 shadow-xl" asChild data-id="element-1952" data-mp="2uk95zk868li4">
                  <Link to="/candidater" data-id="element-1953" data-mp="2lj1i7w3a732a">
                    Je candidate maintenant
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1954" data-mp="2gkq95nkt8wl4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-white/40 text-white hover:bg-white hover:text-primary-dark text-lg px-8 py-7" asChild data-id="element-1955" data-mp="1a5pctgrvice0">
                  <Link to="/entreprises" data-id="element-1956" data-mp="3lsj3x0jn6ry0">Je suis une entreprise</Link>
                </Button>
              </div>
            </div>
          </section>
        </>}
    </div>;
}